import { HiChevronRight } from "react-icons/hi";
import { LuSquarePlus, LuUsers } from "react-icons/lu";
import { MdOutlineInbox } from "react-icons/md";
import { RiDeleteBinLine } from "react-icons/ri";
import { TbEye, TbEdit } from "react-icons/tb";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../DynamicComponents/SnackbarContext";
import apiEndpoints from "../../apiconfig";
import axios from "axios";
import EditGroupModal from "./EditGroupModal";

const ManageGroups = () => {
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroupDetails, setSelectedGroupDetails] = useState(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(null); // ✅ NEW

  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem("token");
  };

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(apiEndpoints.getGroups, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = res.data?.data || [];
      setGroups(data);
      setFilteredGroups(data);
    } catch (err) {
      showSnackbar("Failed to load groups.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDetails = async (groupName) => {
    try {
      const token = getToken();
      const res = await axios.post(
        apiEndpoints.addContact,
        {
          action: "get_group_contacts",
          group_name: groupName,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.data?.status && Array.isArray(res.data?.contacts)) {
        setSelectedGroupDetails({
          name: groupName,
          contacts: res.data.contacts,
        });
      } else {
        showSnackbar("No contacts found for this group.", "info");
      }
    } catch (err) {
      showSnackbar("Failed to fetch group details.", "error");
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredGroups(groups);
    } else {
      const filtered = groups.filter((g) =>
        g.groupname?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredGroups(filtered);
    }
  }, [searchQuery, groups]);

  const confirmBulkDeleteAction = async () => {
    setConfirmBulkDelete(false); // close modal

    try {
      const token = getToken();
      for (const g of selectedGroups) {
        await axios.post(apiEndpoints.getGroups, {
          action: "delete_group",
          group: g,
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      showSnackbar(`${selectedGroups.length} group(s) deleted successfully.`, "success");
      setSelectedGroups([]);
      fetchGroups();
    } catch (err) {
      showSnackbar("Failed to delete selected groups.", "error");
    }
  };

  // ✅ New: Confirm delete action
  const confirmDeleteAction = async () => {
    const groupName = confirmDeleteGroup;
    setConfirmDeleteGroup(null);

    try {
      const token = getToken();
      await axios.post(
        apiEndpoints.getGroups,
        {
          action: "delete_group",
          group: groupName,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showSnackbar(`Group "${groupName}" deleted successfully.`, "success");
      fetchGroups();
    } catch (err) {
      showSnackbar("Failed to delete group.", "error");
    }
  };

  const handleDelete = (groupName) => {
    setConfirmDeleteGroup(groupName); // OPEN MODAL
  };

  const handleBulkDelete = () => {
    if (selectedGroups.length === 0) {
      showSnackbar("Please select at least one group to delete.", "warning");
      return;
    }
    setConfirmBulkDelete(true); // open modal
  };


  const toggleSelectAll = (e) => {
    setSelectedGroups(e.target.checked ? filteredGroups.map((g) => g.groupname) : []);
  };

  const toggleSelectGroup = (groupname, checked) => {
    setSelectedGroups(
      checked
        ? [...selectedGroups, groupname]
        : selectedGroups.filter((name) => name !== groupname)
    );
  };

  const handleSearch = (e) => setSearchQuery(e.target.value);
  const clearSearch = () => setSearchQuery("");

  const handleGroupEditSuccess = (message = "Group updated successfully") => {
    showSnackbar(message, "success");
    fetchGroups();
  };

  const handleContactAddedToGroup = (contactName) => {
    showSnackbar(`Contact "${contactName}" added to group successfully`, "success");
    fetchGroups();
  };

  return (
    <div className="min-h-screen bg-gray-50 font-['Montserrat'] p-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 gap-3">
        <div>
          <h2 className="text-3xl font-semibold text-gray-800">Manage Groups</h2>
          <div className="flex items-center text-sm text-gray-500 mt-1">
            <span>Home</span>
            <HiChevronRight className="mx-1 text-gray-400" />
            <span className="text-[#25D366] font-medium">Groups</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate("/allcontacts")}
            className="flex items-center bg-[#25D366] hover:bg-[#1cb85c] text-white px-3 py-2 rounded-md text-sm font-medium shadow-sm"
          >
            <LuUsers className="mr-2" /> Manage Contacts
          </button>
          <button
            onClick={handleBulkDelete}
            className="flex items-center bg-red-100 text-red-700 hover:bg-red-200 px-3 py-2 rounded-md text-sm font-medium shadow-sm"
          >
            <RiDeleteBinLine className="mr-2" /> Delete
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white shadow-sm rounded-md p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="relative w-full md:w-1/2">
          <input
            type="text"
            placeholder="Search groups..."
            value={searchQuery}
            onChange={handleSearch}
            className="border border-gray-300 rounded-md w-full py-2 px-4 pr-10 focus:ring-2 focus:ring-[#25D366] outline-none"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white shadow-md rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left min-w-[600px]">
            <thead className="bg-gray-100 text-gray-700 font-medium">
              <tr>
                <th className="px-4 py-2">
                  <input
                    type="checkbox"
                    checked={
                      selectedGroups.length === filteredGroups.length && filteredGroups.length > 0
                    }
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="px-4 py-2">S.No</th>
                <th className="px-4 py-2">Group Name</th>
                <th className="px-4 py-2">Total Contacts</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {filteredGroups.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-gray-500">
                    <MdOutlineInbox className="mx-auto text-5xl text-gray-400 mb-3" />
                    <p>{searchQuery ? "No groups found" : "No groups available"}</p>
                  </td>
                </tr>
              ) : (
                filteredGroups.map((group, index) => (
                  <tr key={group.groupname} className="hover:bg-gray-50 transition-all">
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selectedGroups.includes(group.groupname)}
                        onChange={(e) => toggleSelectGroup(group.groupname, e.target.checked)}
                      />
                    </td>
                    <td className="px-4 py-2">{index + 1}</td>
                    <td className="px-4 py-2">{group.groupname}</td>
                    <td className="px-4 py-2">{group.total_contacts}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => fetchGroupDetails(group.groupname)}
                        className="text-yellow-600 hover:text-yellow-700 p-1"
                      >
                        <TbEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(group.groupname)}
                        className="text-red-600 hover:text-red-700 p-1"
                      >
                        <RiDeleteBinLine />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {selectedGroupDetails && (
        <EditGroupModal
          group={selectedGroupDetails}
          onClose={() => setSelectedGroupDetails(null)}
          refreshGroups={fetchGroups}
          onSuccess={handleGroupEditSuccess}
          onContactAdded={handleContactAddedToGroup}
        />
      )}

      {/* ✅ Confirmation Modal */}
      {confirmDeleteGroup && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl">

            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Delete Group?
            </h2>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the group{" "}
              <span className="font-semibold">"{confirmDeleteGroup}"</span>?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDeleteGroup(null)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium"
              >
                Cancel
              </button>

              <button
                onClick={confirmDeleteAction}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmBulkDelete && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl">

            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Delete Selected Groups?
            </h2>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete
              <span className="font-semibold"> {selectedGroups.length} </span>
              group(s)?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmBulkDelete(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium"
              >
                Cancel
              </button>

              <button
                onClick={confirmBulkDeleteAction}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
              >
                Delete All
              </button>
            </div>

          </div>
        </div>
      )}


    </div>
  );
};

export default ManageGroups;
